import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { BlogEntryDTO } from '../DTO/BlogEntryDTO';
import { ProjectDTO } from '../DTO/ProjectDTO';
import { DetailSkeleton } from './Skeleton';
import { MarkdownRenderer } from './MarkdownRenderer';
import { formatDate, readingTime, stripLeadingTitle } from '../lib/format';

export default function BlogDetailComponent() {
    const { slug }              = useParams<{ slug: string }>();
    const [post, setPost]       = useState<BlogEntryDTO | null>(null);
    const [projectName, setProjectName] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [error, setError]     = useState(false);

    useEffect(() => {
        axios.get<BlogEntryDTO>(`/api/blog/slug/${slug}`)
            .then(res => setPost(res.data))
            .catch(err => err.response?.status === 404 ? setNotFound(true) : setError(true))
            .finally(() => setLoading(false));
    }, [slug]);

    // Name of the linked project, so the link can say what it points to.
    useEffect(() => {
        if (!post?.projectSlug) return;
        axios.get<ProjectDTO>(`/api/projects/slug/${post.projectSlug}`)
            .then(res => setProjectName(res.data.name))
            .catch(() => setProjectName(null));
    }, [post?.projectSlug]);

    if (loading)
        return <DetailSkeleton />;

    if (notFound || error || !post)
        return (
            <div className="page-container py-24">
                <h1 className="text-3xl mb-3">{notFound ? 'Post not found' : 'This post could not be loaded'}</h1>
                <p className="text-muted mb-6">
                    {notFound
                        ? 'There is no post at this address. It may have been renamed or unpublished.'
                        : 'Reload the page to try again.'}
                </p>
                <Link to="/blog" className="link font-label">Back to all posts</Link>
            </div>
        );

    // Seed content is indented; strip leading whitespace per line as before.
    const content = stripLeadingTitle(post.content.replace(/^[ \t]+/gm, ''), post.title);

    return (
        <article>
            <header className="page-container pt-10 md:pt-14 pb-10">
                <Link to="/blog" className="link font-label inline-flex items-center gap-1 mb-8">
                    <ChevronLeft size={16} aria-hidden />
                    All posts
                </Link>
                <h1 className="text-[clamp(2.25rem,5vw,3.5rem)] leading-[1.05] max-w-[48rem] m-0">{post.title}</h1>
            </header>

            <div className="page-container pb-20 grid gap-10 md:grid-cols-[12rem_1fr] md:gap-12">
                <aside>
                    <dl className="m-0 grid grid-cols-2 gap-x-6 gap-y-5 md:grid-cols-1 md:sticky md:top-24">
                        <div>
                            <dt className="label mb-1">Published</dt>
                            <dd className="m-0 font-label text-base text-ink">
                                <time dateTime={post.publishDate}>{formatDate(post.publishDate)}</time>
                            </dd>
                        </div>
                        <div>
                            <dt className="label mb-1">Reading time</dt>
                            <dd className="m-0 font-label text-base text-ink">{readingTime(post.content)}</dd>
                        </div>
                        {post.projectSlug && (
                            <div className="col-span-2 md:col-span-1">
                                <dt className="label mb-1">Project</dt>
                                <dd className="m-0 font-label text-base">
                                    <Link to={`/projects/${post.projectSlug}`} className="link">
                                        {projectName ?? 'View the project'}
                                    </Link>
                                </dd>
                            </div>
                        )}
                    </dl>
                </aside>

                <div className="min-w-0">
                    <MarkdownRenderer markdown={content} />
                    <div className="max-w-[42rem] mt-14 pt-6 border-t border-rule">
                        <Link to="/blog" className="link font-label">Back to all posts</Link>
                    </div>
                </div>
            </div>
        </article>
    );
}
